// istanbul ignore file
import React, { useState, useEffect, useRef, useCallback } from 'react'
import Modal from '../../components/Modal'
import { type TopIssuesForPosition } from './types'
import { nortBadge } from './GeoJsonMap'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { changeAssetId } from '../device-details/redux/slices/assetIdSlice'
import LanguageStrings from '../../i18n/locales'
import { CircularProgress } from '@mui/material'

interface InfoModelProps {
  markerData: TopIssuesForPosition[]
  handleModalClose: () => void
}

/**
 * Custom hook for infinite scrolling functionality
 */
function useInfiniteScroll<T> (allItems: T[], itemsPerPage = 50): {
  displayData: T[]
  isFetching: boolean
  loaderRef: React.RefObject<HTMLDivElement>
  hasMoreItems: boolean
} {
  const [displayData, setDisplayData] = useState<T[]>([])
  const [itemsToShow, setItemsToShow] = useState(itemsPerPage)
  const [isFetching, setIsFetching] = useState(false)
  const loaderRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Update displayed data when items change or more items are requested
  useEffect(() => {
    setDisplayData(allItems.slice(0, itemsToShow))
    setIsFetching(false)
  }, [allItems, itemsToShow])

  // Load more items when triggered
  const loadMoreItems = useCallback(() => {
    if (!isFetching && allItems.length > itemsToShow) {
      setIsFetching(true)
      setItemsToShow(prev => prev + itemsPerPage)
    }
  }, [isFetching, allItems.length, itemsToShow, itemsPerPage])

  // Observer callback for intersection detection
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries
    if (entry?.isIntersecting && allItems.length > itemsToShow) {
      loadMoreItems()
    }
  }, [loadMoreItems, allItems.length, itemsToShow])

  // Set up and clean up the IntersectionObserver
  useEffect(() => {
    // Clean up existing observer
    if (observerRef.current !== null) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    // Don't create new observer if we've loaded all items
    if (allItems.length <= itemsToShow) return

    // Create and set up new observer
    const loaderElement = loaderRef.current
    if (loaderElement !== null) {
      observerRef.current = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: '100px',
        threshold: 0
      })
      observerRef.current.observe(loaderElement)
    }

    return () => observerRef.current?.disconnect()
  }, [handleObserver, itemsToShow, allItems.length])

  return { displayData, isFetching, loaderRef, hasMoreItems: allItems.length > itemsToShow }
}

/**
 * Component for the loading indicator at the bottom of the list
 */
const LoadingIndicator = React.forwardRef<HTMLDivElement, { isFetching: boolean, hasMoreItems: boolean }>(
  ({ isFetching, hasMoreItems }, ref) => (
    <div
      ref={ref}
      style={{
        height: '30px',
        padding: '10px 0',
        margin: '5px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%'
      }}
    >
      {isFetching && <CircularProgress size={20} />}
      {!isFetching && hasMoreItems && <div style={{ height: '5px', width: '100%' }} />}
    </div>
  )
)

// Add display name to fix lint error
LoadingIndicator.displayName = 'LoadingIndicator'

/**
 * TableRow component for issue items
 */
interface TableRowProps {
  row: TopIssuesForPosition
  onViewDetails: (assetId: string, assetName: string) => void
  tdrStyle: React.CSSProperties
}

const IssueTableRow: React.FC<TableRowProps> = ({ row, onViewDetails, tdrStyle }) => {
  const MapString = LanguageStrings().Maps.infoPopUp

  return (
    <tr>
      <td style={{ margin: '1em 0 0 0', fontSize: '14px', ...tdrStyle, borderBottom: '1px solid #000', textAlign: 'center' }}>
        {row.assetName}
      </td>
      <td style={{ margin: '1em 0 0 0', fontSize: '14px', ...tdrStyle, borderBottom: '1px solid #000', textAlign: 'center' }}>
        {row.alarmName}
      </td>
      <td style={{ margin: '1em 0 0 0', fontSize: '14px', ...tdrStyle, borderBottom: '1px solid #000', textAlign: 'center' }}>
        <img src={nortBadge(row.severity)} alt={`${row.severity} severity`} />
      </td>
      <td
        onClick={() => { onViewDetails(row.assetId, row.assetName) }}
        style={{ margin: '1em 0 0 0', fontSize: '14px', ...tdrStyle, borderBottom: '1px solid #000', textAlign: 'center', color: '#64c3ff', cursor: 'pointer' }}
      >
        {MapString.ViewDetails}
      </td>
    </tr>
  )
}

/**
 * Main component for the info modal with infinite loading
 */
export const InfoModalWithInfiniteLoading: React.FC<InfoModelProps> = ({ markerData, handleModalClose }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const MapString = LanguageStrings().Maps.infoPopUp

  // Common styles
  const tdrStyle: React.CSSProperties = {
    padding: '10px 0'
  }

  // Set up infinite scrolling
  const { displayData, isFetching, loaderRef, hasMoreItems } = useInfiniteScroll<TopIssuesForPosition>(markerData)

  // Handle navigation to device details
  const handleDeviceDetailClick = (assetId: string, assetName: string): void => {
    handleModalClose()
    dispatch(changeAssetId(assetId))
    sessionStorage.setItem('paramAssetId', assetId != null ? assetId.toString() : '')
    sessionStorage.setItem('paramAssetName', assetName != null ? assetName.toString() : '')
    navigate('/devicedetails')
  }

  // Table header cell style for consistent appearance
  const headerCellStyle = (base: React.CSSProperties = {}): React.CSSProperties => ({
    margin: '1em 0 0 0',
    fontSize: '14px',
    ...tdrStyle,
    borderBottom: '1px solid #000',
    textAlign: 'center',
    fontWeight: 700,
    ...base
  })

  return (
    <Modal
      sx={{ paddingBottom: '10px', zIndex: 1000000 }}
      height="auto"
      width="50vw"
      title={`${MapString.TopIssues} (${markerData.length})`}
      onClose={handleModalClose}
    >
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <table style={{ width: '100%', padding: '10px 5px 0 0' }}>
          <thead>
            <tr>
              <td style={headerCellStyle()}>{MapString.AssetName}</td>
              <td style={headerCellStyle()}>{MapString.AlarmName}</td>
              <td style={headerCellStyle()}>{MapString.Severity}</td>
              <td style={headerCellStyle()}>{MapString.Action}</td>
            </tr>
          </thead>
          <tbody>
            {displayData.length > 0
              ? displayData.map((row, index) => (
                <IssueTableRow
                  key={index}
                  row={row}
                  onViewDetails={handleDeviceDetailClick}
                  tdrStyle={tdrStyle}
                />
              ))
              : null}
          </tbody>
        </table>

        <LoadingIndicator
          ref={loaderRef}
          isFetching={isFetching}
          hasMoreItems={hasMoreItems}
        />
      </div>
    </Modal>
  )
}
